import { useState, useMemo, useEffect, useCallback } from 'react';
import { ShoppingCart, X, Plus, Minus, Search } from 'lucide-react';
import { useShoppingListStore } from '@/stores/shopping-list-store';
import { CATEGORY_LABELS, type InventoryCategory } from '@/stores/inventory-store';

// Vanliga avdelningar i en supermarket
const COMMON_AISLES = [
  'Frukt & Grönt',
  'Mejeri',
  'Kött & Chark',
  'Fisk & Skaldjur',
  'Bröd & Bageri',
  'Konserver',
  'Torrvaror',
  'Fryst',
  'Drycker',
  'Godis & Snacks',
  'Kemi & Städning',
  'Hygien',
  'Djurmat',
  'Hushåll',
  'Baby',
];

// Kategori-anpassade avdelningar
const CATEGORY_AISLES: Record<string, string[]> = {
  mat: ['Frukt & Grönt', 'Mejeri', 'Kött & Chark', 'Fisk & Skaldjur', 'Bröd & Bageri', 'Konserver', 'Torrvaror', 'Fryst', 'Drycker', 'Godis & Snacks'],
  medicin: ['Apotek', 'Hygien', 'Hälsokost'],
  hygien: ['Hygien', 'Kemi & Städning', 'Baby'],
  verktyg: ['Hushåll', 'Järnhandel', 'Byggvaror'],
  kommunikation: ['Elektronik', 'Hushåll'],
  varme: ['Hushåll', 'Järnhandel', 'Byggvaror'],
  ljus: ['Hushåll', 'Järnhandel'],
  dokument: ['Kontorsmaterial', 'Hushåll'],
  ovrigt: [...COMMON_AISLES],
};

interface AddToShoppingListModalProps {
  open: boolean;
  itemName: string;
  onClose: () => void;
}

export default function AddToShoppingListModal({ open, itemName, onClose }: AddToShoppingListModalProps) {
  const { lists: shoppingLists, createItem: createShoppingItem, createList: createShoppingList } = useShoppingListStore();
  const shoppingItems = useShoppingListStore(state => state.items);

  const [shoppingQuantity, setShoppingQuantity] = useState(1);
  const [selectedListId, setSelectedListId] = useState<string | null>(null);
  const [shoppingCategory, setShoppingCategory] = useState<InventoryCategory>('verktyg');
  const [shoppingNotes, setShoppingNotes] = useState('');
  const [shoppingStore, setShoppingStore] = useState('');
  const [shoppingAisle, setShoppingAisle] = useState('');
  const [shoppingPrice, setShoppingPrice] = useState('0');
  const [showNewListForm, setShowNewListForm] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [showStoreSuggestions, setShowStoreSuggestions] = useState(false);
  const [storeSuggestionSearch, setStoreSuggestionSearch] = useState('');
  const [showAisleSuggestions, setShowAisleSuggestions] = useState(false);
  const [aisleSuggestionSearch, setAisleSuggestionSearch] = useState('');

  const existingStores = useMemo(() => {
    const stores = new Set<string>();
    shoppingItems.forEach(item => {
      if (item.store) stores.add(item.store);
    });
    return Array.from(stores).sort();
  }, [shoppingItems]);

  const filteredStores = useMemo(() => {
    if (!storeSuggestionSearch) return existingStores;
    return existingStores.filter(s =>
      s.toLowerCase().includes(storeSuggestionSearch.toLowerCase())
    );
  }, [existingStores, storeSuggestionSearch]);

  const filteredAisles = useMemo(() => {
    const aisles = CATEGORY_AISLES[shoppingCategory] || COMMON_AISLES;
    if (!aisleSuggestionSearch) return aisles;
    return aisles.filter(a =>
      a.toLowerCase().includes(aisleSuggestionSearch.toLowerCase())
    );
  }, [aisleSuggestionSearch, shoppingCategory]);

  const resetState = useCallback(() => {
    setShoppingQuantity(1);
    setShoppingCategory('verktyg');
    setShoppingNotes('');
    setShoppingStore('');
    setShoppingAisle('');
    setShoppingPrice('0');
    setShowNewListForm(false);
    setNewListName('');
    setShowStoreSuggestions(false);
    setStoreSuggestionSearch('');
    setShowAisleSuggestions(false);
    setAisleSuggestionSearch('');
    const primary = shoppingLists.find(l => l.isPrimary);
    setSelectedListId(primary?.id || shoppingLists[0]?.id || null);
  }, [shoppingLists]);

  useEffect(() => {
    if (open) {
      resetState();
    }
  }, [open, resetState]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleAdd = async () => {
    if (!itemName.trim()) return;

    if (showNewListForm) {
      if (!newListName.trim()) return;
      const newList = await createShoppingList({
        name: newListName,
        isPrimary: shoppingLists.length === 0,
      });
      if (newList) {
        await createShoppingItem({
          listId: newList.id,
          name: itemName,
          category: CATEGORY_LABELS[shoppingCategory],
          notes: shoppingNotes || undefined,
          quantity: shoppingQuantity,
          price: parseFloat((shoppingPrice || '0').replace(',', '.')) || 0,
          store: shoppingStore || undefined,
          aisle: shoppingAisle || undefined,
        });
      }
    } else {
      if (!selectedListId) return;
      await createShoppingItem({
        listId: selectedListId,
        name: itemName,
        category: CATEGORY_LABELS[shoppingCategory],
        notes: shoppingNotes || undefined,
        quantity: shoppingQuantity,
        price: parseFloat((shoppingPrice || '0').replace(',', '.')) || 0,
        store: shoppingStore || undefined,
        aisle: shoppingAisle || undefined,
      });
    }

    handleClose();
  };

  if (!open) {
    return null;
  }

  return (
    <>
      {/* Huvudmodal - layout och styling matchar ChecklistsPage */}
      <div className="fixed z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, margin: 0 }}>
        <div className="w-full max-w-md rounded-xl bg-card border border-border p-6 my-8 max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/20">
                <ShoppingCart className="h-6 w-6 text-primary" />
              </div>
              <h2 className="text-xl font-semibold">Lägg till i inköpslista</h2>
            </div>
            <button
              onClick={handleClose}
              className="p-1 rounded hover:bg-muted flex-shrink-0"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="mb-4">
            <p className="text-sm text-muted-foreground mb-1">Artikel</p>
            <p className="font-medium">{itemName}</p>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Kategori</label>
            <select
              value={shoppingCategory}
              onChange={(e) => setShoppingCategory(e.target.value as InventoryCategory)}
              className="w-full px-3 py-2 rounded-lg border border-border bg-background"
            >
              <option value="mat">{CATEGORY_LABELS.mat}</option>
              <option value="medicin">{CATEGORY_LABELS.medicin}</option>
              <option value="hygien">{CATEGORY_LABELS.hygien}</option>
              <option value="verktyg">{CATEGORY_LABELS.verktyg}</option>
              <option value="kommunikation">{CATEGORY_LABELS.kommunikation}</option>
              <option value="varme">{CATEGORY_LABELS.varme}</option>
              <option value="ljus">{CATEGORY_LABELS.ljus}</option>
              <option value="dokument">{CATEGORY_LABELS.dokument}</option>
              <option value="ovrigt">{CATEGORY_LABELS.ovrigt}</option>
            </select>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">
              Beskrivning (valfritt)
              <span className="ml-2 text-xs text-muted-foreground">{shoppingNotes.length}/200</span>
            </label>
            <textarea
              value={shoppingNotes}
              maxLength={200}
              onChange={(e) => setShoppingNotes(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 rounded-lg border border-border bg-background resize-none"
              placeholder="T.ex. ekologisk, glutenfri…"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Antal</label>
            <div className="flex items-center gap-2 max-w-xs">
              <button
                onClick={() => setShoppingQuantity(Math.max(1, shoppingQuantity - 1))}
                className="p-2 rounded-lg border border-border hover:bg-muted transition-colors flex-shrink-0"
              >
                <Minus className="h-4 w-4" />
              </button>
              <input
                type="number"
                min="1"
                max="1000"
                value={shoppingQuantity}
                onChange={(e) => setShoppingQuantity(Math.min(1000, Math.max(1, parseInt(e.target.value) || 1)))}
                className="w-20 px-3 py-2 rounded-lg border border-border bg-background text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              <button
                onClick={() => setShoppingQuantity(Math.min(1000, shoppingQuantity + 1))}
                className="p-2 rounded-lg border border-border hover:bg-muted transition-colors flex-shrink-0"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Pris (valfritt)</label>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setShoppingPrice(prev => { const cur = parseFloat((prev || '0').replace(',', '.')) || 0; const n = Math.max(0, cur - 100); return n % 1 === 0 ? String(n) : String(n).replace('.', ','); })}
                disabled={parseFloat((shoppingPrice || '0').replace(',', '.')) <= 0}
                className="p-2 rounded-lg border border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Minus className="h-4 w-4" />
              </button>
              <div className="flex items-center gap-1">
                <input
                  type="text"
                  inputMode="decimal"
                  value={shoppingPrice}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/[^0-9,.]/g, '').replace('.', ',').replace(/,{2,}/g, ',');
                    const parts = raw.split(',');
                    const cleaned = parts[0].slice(0, 7) + (parts.length > 1 ? ',' + parts[1].slice(0, 2) : '');
                    if (cleaned === '' || cleaned === ',') { setShoppingPrice('0'); return; }
                    const numVal = parseFloat(cleaned.replace(',', '.'));
                    if (!isNaN(numVal) && numVal <= 1000000) setShoppingPrice(cleaned);
                  }}
                  className="w-24 px-3 py-2 text-center rounded-lg border border-border bg-background"
                />
                <span className="text-sm text-muted-foreground">kr</span>
              </div>
              <button
                type="button"
                onClick={() => setShoppingPrice(prev => { const cur = parseFloat((prev || '0').replace(',', '.')) || 0; const n = Math.min(1000000, cur + 100); return n % 1 === 0 ? String(n) : String(n).replace('.', ','); })}
                className="p-2 rounded-lg border border-border hover:bg-muted transition-colors"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">
              Butik (valfritt)
              <span className="ml-2 text-xs text-muted-foreground">{shoppingStore.length}/50</span>
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                maxLength={50}
                value={shoppingStore}
                onChange={(e) => setShoppingStore(e.target.value)}
                className="flex-1 px-3 py-2 rounded-lg border border-border bg-background"
                placeholder="T.ex. ICA Maxi"
              />
              <button
                type="button"
                onClick={() => {
                  setStoreSuggestionSearch('');
                  setShowStoreSuggestions(true);
                }}
                className="p-2 rounded-lg border border-border hover:bg-muted transition-colors"
                title="Visa befintliga butiker"
              >
                <Search className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">
              Hylla/Avdelning (valfritt)
              <span className="ml-2 text-xs text-muted-foreground">{shoppingAisle.length}/50</span>
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                maxLength={50}
                value={shoppingAisle}
                onChange={(e) => setShoppingAisle(e.target.value)}
                className="flex-1 px-3 py-2 rounded-lg border border-border bg-background"
                placeholder="T.ex. Mejeri"
              />
              <button
                type="button"
                onClick={() => {
                  setAisleSuggestionSearch('');
                  setShowAisleSuggestions(true);
                }}
                className="p-2 rounded-lg border border-border hover:bg-muted transition-colors"
                title="Visa avdelningar"
              >
                <Search className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">
              Välj inköpslista
            </label>
            {showNewListForm ? (
              <div className="space-y-3">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-muted-foreground">{newListName.length}/25</span>
                  </div>
                  <input
                    type="text"
                    maxLength={25}
                    value={newListName}
                    onChange={(e) => setNewListName(e.target.value)}
                    placeholder="Namn på ny lista"
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary focus:border-transparent"
                    autoFocus
                  />
                </div>
                <button
                  onClick={() => {
                    setShowNewListForm(false);
                    setNewListName('');
                    const primary = shoppingLists.find(l => l.isPrimary);
                    setSelectedListId(primary?.id || shoppingLists[0]?.id || null);
                  }}
                  className="text-sm text-primary hover:underline"
                >
                  
                  ← Tillbaka till befintliga listor
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {shoppingLists.length > 0 && (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {shoppingLists.map(list => (
                      <label
                        key={list.id}
                        className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-muted/50 cursor-pointer transition-colors"
                      >
                        <input
                          type="radio"
                          name="shopping-list"
                          checked={selectedListId === list.id}
                          onChange={() => setSelectedListId(list.id)}
                          className="text-primary focus:ring-primary"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium soft-wrap">{list.name}</span>
                            {list.isPrimary && (
                              <span className="text-yellow-500" title="Primär lista">
                                ★
                              </span>
                            )}
                          </div>
                          {list.store && (
                            <span className="text-xs text-muted-foreground">{list.store}</span>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                )}

                <button
                  onClick={() => setShowNewListForm(true)}
                  className="w-full px-4 py-2 rounded-lg border border-dashed border-border transition-colors text-sm flex items-center justify-center gap-2"
                  style={{ backgroundColor: '#67724e', color: '#fff' }}
                  onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = '#56633f')}
                  onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = '#67724e')}
                >
                  <Plus className="h-4 w-4" />
                  Skapa ny inköpslista
                </button>
              </div>
            )}
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleClose}
              className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
            >
              Avbryt
            </button>
            <button
              onClick={handleAdd}
              disabled={showNewListForm ? !newListName.trim() : !selectedListId}
              className="flex-1 px-4 py-2 rounded-lg btn-primary-custom transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Lägg till
            </button>
          </div>
        </div>
      </div>

      {/* Store Suggestions Modal */}
      {showStoreSuggestions && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4 overflow-y-auto">
          <div className="w-full max-w-md rounded-xl bg-card border border-border p-4 max-h-[80vh] flex flex-col my-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Välj butik</h3>
              <button onClick={() => setShowStoreSuggestions(false)} className="p-1 rounded hover:bg-muted">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mb-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  value={storeSuggestionSearch}
                  onChange={(e) => setStoreSuggestionSearch(e.target.value)}
                  placeholder="Sök butik..."
                  className="w-full pl-10 pr-3 py-2 rounded-lg border border-border bg-background"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto space-y-1">
              {filteredStores.map((store) => (
                <button
                  key={store}
                  onClick={() => {
                    setShoppingStore(store);
                    setShowStoreSuggestions(false);
                  }}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-muted transition-colors"
                >
                  {store}
                </button>
              ))}
              {filteredStores.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  Inga butiker hittades
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Aisle Suggestions Modal */}
      {showAisleSuggestions && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4 overflow-y-auto">
          <div className="w-full max-w-md rounded-xl bg-card border border-border p-4 max-h-[80vh] flex flex-col my-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Välj hylla/avdelning</h3>
              <button onClick={() => setShowAisleSuggestions(false)} className="p-1 rounded hover:bg-muted">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mb-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  value={aisleSuggestionSearch}
                  onChange={(e) => setAisleSuggestionSearch(e.target.value)}
                  placeholder="Sök hylla/avdelning..."
                  className="w-full pl-10 pr-3 py-2 rounded-lg border border-border bg-background"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto space-y-1">
              {filteredAisles.map((aisle) => (
                <button
                  key={aisle}
                  onClick={() => {
                    setShoppingAisle(aisle);
                    setShowAisleSuggestions(false);
                  }}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-muted transition-colors"
                >
                  {aisle}
                </button>
              ))}
              {filteredAisles.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  Inga avdelningar hittades
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
